/**
 * BackfillParallelPage — /omega/backfill/parallel
 *
 * Sprint 36: Shows the 5-parallel-worker backfill coordinator status.
 * Polls GET /api/v1/admin/backfill/parallel-status every 30 s.
 * Provides per-source pause/resume toggles.
 * Founder-only (isOmegaAdmin guard).
 */

import { Navigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { apiFetch } from '@/api/http';

// ── Types ─────────────────────────────────────────────────────────────────────

interface WorkerStatus {
  source: string;
  status: string;
  offset: number;
  inserted: number;
  rate_per_min: number;
  paused: boolean;
}

interface GuardStatus {
  healthcheck_ok: boolean;
  healthcheck_latency_ms: number;
  throttled: boolean;
}

interface CoordinatorStatus {
  running: boolean;
  paused: boolean;
  total_atoms_inserted: number;
  combined_rate_per_min: number;
  eta_hours: number;
  workers: Record<string, WorkerStatus>;
  interference_guards: GuardStatus;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function Badge({
  label,
  color,
}: {
  label: string;
  color: 'green' | 'red' | 'blue' | 'gray';
}) {
  const bg: Record<string, string> = {
    green: '#16a34a',
    red: '#dc2626',
    blue: '#2563eb',
    gray: '#9ca3af',
  };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 4,
        fontWeight: 600,
        fontSize: 13,
        background: bg[color],
        color: '#fff',
      }}
    >
      {label}
    </span>
  );
}

function workerBadge(w: WorkerStatus) {
  if (w.paused) return <Badge label="PAUSED" color="gray" />;
  if (w.status === 'running') return <Badge label="RUNNING" color="blue" />;
  if (w.status === 'done') return <Badge label="DONE" color="green" />;
  return <Badge label="PENDING" color="gray" />;
}

const SOURCE_LABELS: Record<string, string> = {
  arxiv: 'arXiv',
  openalex: 'OpenAlex',
  frontiers: 'Frontiers',
  mit_ocw: 'MIT OCW',
  gutenberg: 'Gutenberg',
};

// ── Page ──────────────────────────────────────────────────────────────────────

export function BackfillParallelPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const queryClient = useQueryClient();

  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['backfill-parallel-status'],
    queryFn: () => apiFetch<CoordinatorStatus>('/admin/backfill/parallel-status'),
    refetchInterval: 30_000,
  });

  const pauseAllMutation = useMutation({
    mutationFn: () => apiFetch('/admin/backfill/pause', { method: 'POST' }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['backfill-parallel-status'] }),
  });

  const resumeAllMutation = useMutation({
    mutationFn: () => apiFetch('/admin/backfill/resume', { method: 'POST' }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['backfill-parallel-status'] }),
  });

  const pauseSourceMutation = useMutation({
    mutationFn: (source: string) =>
      apiFetch(`/admin/backfill/workers/${source}/pause`, { method: 'POST' }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['backfill-parallel-status'] }),
  });

  const resumeSourceMutation = useMutation({
    mutationFn: (source: string) =>
      apiFetch(`/admin/backfill/workers/${source}/resume`, { method: 'POST' }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['backfill-parallel-status'] }),
  });

  const globalMutating =
    pauseAllMutation.isPending || resumeAllMutation.isPending;

  const ESTIMATED_TOTAL = 500_000;

  return (
    <div style={{ padding: 24, maxWidth: 1000 }}>
      <h1 style={{ marginBottom: 4 }}>Parallel Backfill Coordinator</h1>
      <p style={{ color: '#6b7280', marginBottom: 6 }}>
        Sprint 36 — 5 independent workers repopulating atom_tags.db for
        historical atoms.{' '}
        {dataUpdatedAt > 0 && (
          <>Last updated: {new Date(dataUpdatedAt).toLocaleTimeString()}.</>
        )}
      </p>
      <p style={{ color: '#6b7280', marginBottom: 20, fontSize: 13 }}>
        <Link to="/omega/system/backfill" style={{ color: '#2563eb' }}>
          Sprint 35 single-threaded view
        </Link>
      </p>

      {isLoading && <p>Loading coordinator status…</p>}
      {error && (
        <p style={{ color: '#dc2626' }}>Failed to load coordinator status.</p>
      )}

      {data && (
        <>
          {/* ── Combined metrics ─────────────────────────────────────────── */}
          <div
            style={{
              display: 'flex',
              gap: 24,
              marginBottom: 24,
              flexWrap: 'wrap',
            }}
          >
            {[
              {
                label: 'Total atoms inserted',
                value: data.total_atoms_inserted.toLocaleString(),
              },
              {
                label: 'Combined rate',
                value: `${data.combined_rate_per_min} atoms/min`,
              },
              {
                label: 'ETA',
                value:
                  data.combined_rate_per_min === 0
                    ? '— (no rate)'
                    : `${data.eta_hours.toLocaleString()} hours`,
              },
              {
                label: 'Progress',
                value: `${((data.total_atoms_inserted / ESTIMATED_TOTAL) * 100).toFixed(1)}%`,
              },
            ].map(({ label, value }) => (
              <div
                key={label}
                style={{
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  padding: '12px 20px',
                  minWidth: 160,
                }}
              >
                <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 4 }}>
                  {label}
                </div>
                <div style={{ fontWeight: 700, fontSize: 20 }}>{value}</div>
              </div>
            ))}
          </div>

          {/* ── Global controls ───────────────────────────────────────────── */}
          <div style={{ marginBottom: 24, display: 'flex', gap: 12 }}>
            <button
              onClick={() => pauseAllMutation.mutate()}
              disabled={globalMutating || data.paused}
              style={{
                padding: '8px 20px',
                background: data.paused ? '#9ca3af' : '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                cursor: data.paused ? 'not-allowed' : 'pointer',
              }}
            >
              {pauseAllMutation.isPending ? 'Pausing all…' : 'Pause All'}
            </button>
            <button
              onClick={() => resumeAllMutation.mutate()}
              disabled={globalMutating || !data.paused}
              style={{
                padding: '8px 20px',
                background: !data.paused ? '#9ca3af' : '#16a34a',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                cursor: !data.paused ? 'not-allowed' : 'pointer',
              }}
            >
              {resumeAllMutation.isPending ? 'Resuming all…' : 'Resume All'}
            </button>
          </div>

          {/* ── Per-source worker rows ────────────────────────────────────── */}
          <h2 style={{ marginBottom: 12 }}>Per-Source Workers</h2>
          <table
            style={{ borderCollapse: 'collapse', marginBottom: 28, width: '100%' }}
          >
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                {[
                  'Source',
                  'Status',
                  'Offset',
                  'Inserted',
                  'Rate (atoms/min)',
                  'Progress',
                  'Control',
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: '8px 12px',
                      border: '1px solid #e5e7eb',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.workers).length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      padding: 12,
                      textAlign: 'center',
                      color: '#6b7280',
                      border: '1px solid #e5e7eb',
                    }}
                  >
                    No worker data yet — coordinator has not started.
                  </td>
                </tr>
              ) : (
                Object.entries(data.workers).map(([key, w]) => {
                  const workerTotal = w.inserted;
                  const workerShare =
                    ESTIMATED_TOTAL > 0
                      ? Math.min(100, (workerTotal / (ESTIMATED_TOTAL / 5)) * 100)
                      : 0;
                  return (
                    <tr key={key}>
                      <td
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                          fontWeight: 600,
                        }}
                      >
                        {SOURCE_LABELS[key] ?? key}
                      </td>
                      <td
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                        }}
                      >
                        {workerBadge(w)}
                      </td>
                      <td
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {w.offset.toLocaleString()}
                      </td>
                      <td
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {w.inserted.toLocaleString()}
                      </td>
                      <td
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                          textAlign: 'right',
                        }}
                      >
                        {w.rate_per_min}
                      </td>
                      <td
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                          minWidth: 120,
                        }}
                      >
                        <div
                          style={{
                            background: '#e5e7eb',
                            borderRadius: 4,
                            height: 8,
                            width: '100%',
                          }}
                        >
                          <div
                            style={{
                              background: '#2563eb',
                              borderRadius: 4,
                              height: 8,
                              width: `${workerShare.toFixed(1)}%`,
                            }}
                          />
                        </div>
                        <span style={{ fontSize: 11, color: '#6b7280' }}>
                          {workerShare.toFixed(1)}%
                        </span>
                      </td>
                      <td
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #e5e7eb',
                        }}
                      >
                        {w.paused ? (
                          <button
                            onClick={() => resumeSourceMutation.mutate(key)}
                            disabled={resumeSourceMutation.isPending}
                            style={{
                              padding: '4px 12px',
                              background: '#16a34a',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              fontWeight: 600,
                              cursor: 'pointer',
                              fontSize: 13,
                            }}
                          >
                            Resume
                          </button>
                        ) : (
                          <button
                            onClick={() => pauseSourceMutation.mutate(key)}
                            disabled={pauseSourceMutation.isPending}
                            style={{
                              padding: '4px 12px',
                              background: '#dc2626',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 4,
                              fontWeight: 600,
                              cursor: 'pointer',
                              fontSize: 13,
                            }}
                          >
                            Pause
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* ── Interference guards ───────────────────────────────────────── */}
          <h2 style={{ marginBottom: 8 }}>Interference Guards</h2>
          <table
            style={{ borderCollapse: 'collapse', marginBottom: 24, width: '100%' }}
          >
            <tbody>
              {[
                {
                  label: 'Healthcheck',
                  value: (
                    <>
                      <Badge
                        label={
                          data.interference_guards.healthcheck_ok ? 'OK' : 'DEGRADED'
                        }
                        color={
                          data.interference_guards.healthcheck_ok ? 'green' : 'red'
                        }
                      />{' '}
                      <span style={{ color: '#6b7280', fontSize: 13 }}>
                        {data.interference_guards.healthcheck_latency_ms} ms
                      </span>
                    </>
                  ),
                },
                {
                  label: 'Throttled',
                  value: (
                    <Badge
                      label={
                        data.interference_guards.throttled ? 'THROTTLED' : 'NORMAL'
                      }
                      color={data.interference_guards.throttled ? 'red' : 'green'}
                    />
                  ),
                },
                {
                  label: 'DB isolation',
                  value: (
                    <span style={{ color: '#16a34a', fontWeight: 600 }}>
                      atom_tags.db only — atoms.db never touched
                    </span>
                  ),
                },
              ].map(({ label, value }) => (
                <tr key={label}>
                  <td
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #e5e7eb',
                      color: '#6b7280',
                      width: 220,
                    }}
                  >
                    {label}
                  </td>
                  <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb' }}>
                    {value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <p style={{ color: '#6b7280', fontSize: 13 }}>
            Batch size: {100} rows per SQLite transaction (guard b). arXiv: 1
            req/3 s · OpenAlex: 5 req/s · Frontiers: 1 req/s · MIT OCW: 1 req/s
            · Gutenberg: 0.5 req/s. Combined ceiling: ~92 docs/min.
          </p>
        </>
      )}
    </div>
  );
}
